////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
//                                                                                                                //
//                                           매스컨셉 임시 조회 페이지                                               //
//                                                                                                                //
//  API ENDPOINTS:                                                                                                //
//                                                                                                                //
//  1. GET /api/mathcepts/temp                                                                                    //
//     - 임시 매스컨셉 조회 및 검색용 API                                                                          //
//     - Request Params:                                                                                          //
//       • description='true' (선택) - description 포함 여부                                                       //
//       • math_type: ["AL", "AN", "GE", "PS"] 중 택1                                                            //
//       • concept_type: ["DEF", "AXM", "IDN", "THM", "MTH", "PRP", "FOR", "NTN"] 중 택1                        //
//       • concept_name: 검색할 개념명 (예: "거듭")                                                                //
//                                                                                                                //
//  2. GET /api/mathcepts/temp/by-concept-ids                                                                    //
//     - 매스컨셉 ID로 조회                                                                                        //
//     - Request Params:                                                                                          //
//       • concept_ids: 쉼표로 구분된 Integer 문자열 (필수) 예: "5,55,555"                                         //
//     - Response: descriptions 필드 포함                                                                         //
//                                                                                                                //
//  3. GET /api/mathcepts/temp/tags                                                                              //
//     - 태그로 매스컨셉 조회                                                                                      //
//     - Request Params:                                                                                          //
//       • description='true' (선택) - description 포함 여부                                                       //
//       • tags: 검색할 태그명 (필수, 2자 이상 100자 미만, DB 문자열과 정확히 일치해야 함)                           //
//                                                                                                                //
//  ENUM DEFINITIONS:                                                                                             //
//                                                                                                                //
//  Math_type (수학 분야):                                                                                        //
//    • AL: 대수학 (Algebra)                                                                                      //
//    • AN: 해석학 (Analysis)                                                                                     //
//    • GE: 기하학 (Geometry)                                                                                     //
//    • PS: 확률과통계 (Probability and Statistics)                                                               //
//                                                                                                                //
//  Concept_types (개념 유형):                                                                                    //
//    • DEF: 정의 (Definition)                                                                                    //
//    • AXM: 공리 (Axiom)                                                                                         //
//    • IDN: 정리 (Identity)                                                                                      //
//    • THM: 이론 (Theorem)                                                                                       //
//    • MTH: 방법론 (Method)                                                                                      //
//    • PRP: 성질 (Property)                                                                                      //
//    • FOR: 공식 (Formula)                                                                                       //
//    • NTN: 표기법 (Notation)                                                                                    //
//                                                                                                                //
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { PageContainer, Card, Button, DataGrid, MathceptDetailModal, useMathceptDetail } from '../components/common';
import { Search, Loader, Download, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import katex from 'katex';
import 'katex/dist/katex.min.css';

function MathceptsTemp() {
  const { getAccessToken } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [mathcepts, setMathcepts] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;
  
  // 상세 정보 모달 (공통 컴포넌트 사용)
  const [selectedItems, setSelectedItems] = useState([]);
  const { isModalOpen: detailModalOpen, loading: detailLoading, data: detailData, fetchAndShow: fetchMathceptDetails, closeModal: closeDetailModal, refreshData: refreshDetailData } = useMathceptDetail({ handleAuth: true });

  // 검색 필터 상태
  const [searchParams, setSearchParams] = useState({
    description: false,
    math_type: '',
    concept_type: '',
    concept_name: '',
    tag: '' // 태그 검색 추가
  });

  // 옵션 정의
  const mathTypeOptions = [
    { value: '', label: '전체' },
    { value: 'AL', label: '대수학' },
    { value: 'AN', label: '해석학' },
    { value: 'GE', label: '기하학' },
    { value: 'PS', label: '확률과통계' }
  ];

  const conceptTypeOptions = [
    { value: '', label: '전체' },
    { value: 'DEF', label: '정의' },
    { value: 'AXM', label: '공리' },
    { value: 'IDN', label: '정리' },
    { value: 'THM', label: '이론' },
    { value: 'MTH', label: '방법론' },
    { value: 'PRP', label: '성질' },
    { value: 'FOR', label: '공식' },
    { value: 'NTN', label: '표기법' }
  ];

  // LaTeX 렌더링 함수
  const renderLatex = useCallback((text) => {
    if (!text) return '';
    
    try {
      // LaTeX 패턴을 찾아서 렌더링
      const latexPattern = /\$([^$]+)\$/g;
      const parts = [];
      let lastIndex = 0;
      let match;

      while ((match = latexPattern.exec(text)) !== null) {
        // LaTeX 이전 텍스트 추가
        if (match.index > lastIndex) {
          parts.push(text.substring(lastIndex, match.index));
        }
        
        // LaTeX 렌더링
        const latex = match[1];
        const html = katex.renderToString(latex, {
          throwOnError: false,
          displayMode: false
        });
        parts.push(`<span class="katex-inline">${html}</span>`);
        
        lastIndex = match.index + match[0].length;
      }
      
      // 마지막 남은 텍스트 추가
      if (lastIndex < text.length) {
        parts.push(text.substring(lastIndex));
      }
      
      return parts.join('');
    } catch (error) {
      console.error('LaTeX rendering error:', error);
      return text;
    }
  }, []);

  // API 호출
  const fetchMathcepts = useCallback(async (customParams = null) => {
    try {
      setLoading(true);
      
      // 토큰 가져오기
      const token = await getAccessToken();
      if (!token) {
        navigate('/signin');
        return;
      }
      
      // 매개변수로 받은 값이 있으면 사용, 없으면 현재 searchParams 사용
      const currentParams = customParams || searchParams;
      
      // 태그 검색인 경우
      if (currentParams.tag) {
        // 태그 검색 API 사용
        const params = new URLSearchParams();
        if (currentParams.description) {
          params.append('description', 'true');
        }
        params.append('tags', currentParams.tag);
        
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/mathcepts/temp/tags?${params}`, {
          method: 'GET',
          headers: {
            'Authorization': token,
            'Content-Type': 'application/json',
          },
        });

        if (response.status === 401) {
          navigate('/signin');
          return;
        }

        if (response.ok) {
          const data = await response.json();
          setMathcepts(data.data || []);
          setTotalCount(data.data?.length || 0);
        } else {
          console.error('Failed to fetch mathcepts by tag:', response.status);
          setMathcepts([]);
          setTotalCount(0);
        }
      } else {
        // 기존 검색 API 사용
        const params = new URLSearchParams();
        if (currentParams.description) {
          params.append('description', 'true');
        }
        if (currentParams.math_type) {
          params.append('math_type', currentParams.math_type);
        }
        if (currentParams.concept_type) {
          params.append('concept_type', currentParams.concept_type);
        }
        if (currentParams.concept_name) {
          params.append('concept_name', currentParams.concept_name);
        }

        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/mathcepts/temp?${params}`, {
          method: 'GET',
          headers: {
            'Authorization': token,
            'Content-Type': 'application/json',
          },
        });

        if (response.status === 401) {
          navigate('/signin');
          return;
        }

        if (response.ok) {
          const data = await response.json();
          setMathcepts(data.data || []);
          setTotalCount(data.data?.length || 0);
        } else {
          console.error('Failed to fetch mathcepts:', response.status);
          setMathcepts([]);
          setTotalCount(0);
        }
      }
    } catch (error) {
      console.error('Error fetching mathcepts:', error);
      setMathcepts([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [getAccessToken, searchParams, navigate]);

  // 초기 로드
  useEffect(() => {
    // 초기 로드 시 빈 파라미터로 전체 리스트 가져오기
    fetchMathcepts({ description: false, math_type: '', concept_type: '', concept_name: '', tag: '' });
  }, []); // 빈 의존성 배열로 한 번만 실행

  // 검색 핸들러
  const handleSearch = () => {
    setCurrentPage(1);
    fetchMathcepts();  // 현재 searchParams 사용
  };

  // 엔터키 검색
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // 페이지네이션된 데이터
  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return mathcepts.slice(startIndex, endIndex);
  }, [mathcepts, currentPage]);

  // 테이블 컬럼 정의
  const columns = [
    {
      key: 'concept_id',
      title: 'ID',
      width: '80px',
      align: 'center'
    },
    {
      key: 'concept_name',
      title: '개념명',
      width: '200px',
      render: (value, item) => (
        <div dangerouslySetInnerHTML={{ __html: renderLatex(item.concept_name) }} />
      )
    },
    {
      key: 'math_type',
      title: '수학분야',
      width: '100px',
      align: 'center',
      render: (value, item) => {
        const option = mathTypeOptions.find(opt => opt.value === item.math_type);
        return option ? option.label : item.math_type;
      }
    },
    {
      key: 'concept_type',
      title: '개념유형',
      width: '100px',
      align: 'center',
      render: (value, item) => {
        const option = conceptTypeOptions.find(opt => opt.value === item.concept_type);
        return option ? option.label : item.concept_type;
      }
    },
    {
      key: 'tags',
      title: '태그',
      width: '200px',
      render: (value, item) => (
        <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
          {item.tags?.map((tag, index) => (
            <span 
              key={index} 
              style={{
                padding: '2px 8px',
                backgroundColor: '#f3f4f6',
                borderRadius: '4px',
                fontSize: '12px',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              onClick={(e) => {
                e.stopPropagation(); // 행 클릭 이벤트와 충돌 방지
                handleTagClick(tag);
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#e5e7eb';
                e.target.style.transform = 'scale(1.05)';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = '#f3f4f6';
                e.target.style.transform = 'scale(1)';
              }}
              title={`"${tag}" 태그로 검색`}
            >
              {tag}
            </span>
          ))}
        </div>
      )
    },
    {
      key: 'order',
      title: '순서',
      width: '80px',
      align: 'center'
    },
    {
      key: 'is_outdated',
      title: '상태',
      width: '80px',
      align: 'center',
      render: (value, item) => (
        <span style={{
          color: item.is_outdated ? '#ef4444' : '#4ABC85',
          fontWeight: 'bold'
        }}>
          {item.is_outdated ? '구버전' : '최신'}
        </span>
      )
    },
    {
      key: 'created_at',
      title: '생성일',
      width: '120px',
      align: 'center',
      render: (value, item) => new Date(item.created_at).toLocaleDateString('ko-KR')
    }
  ];

  // description이 포함된 경우 추가 컬럼
  if (searchParams.description) {
    columns.push({
      key: 'description',
      title: '설명',
      width: '300px',
      render: (value, item) => (
        <div dangerouslySetInnerHTML={{ __html: renderLatex(item.description) }} />
      )
    });
  }

  // CSV 다운로드
  const handleDownloadCSV = () => {
    const headers = columns.map(col => col.title).join(',');
    const rows = mathcepts.map(item => {
      return columns.map(col => {
        const value = item[col.key];
        if (Array.isArray(value)) {
          return value.join(';');
        }
        if (typeof value === 'boolean') {
          return value ? 'true' : 'false';
        }
        return value || '';
      }).join(',');
    });
    
    const csv = [headers, ...rows].join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `mathcepts_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };



  // 행 클릭 핸들러
  const handleRowClick = (row) => {
    if (row.concept_id) {
      fetchMathceptDetails([row.concept_id]);
    }
  };

  // 복수 선택 모드 (필요시 활성화)
  const handleMultipleSelect = () => {
    if (selectedItems.length > 0) {
      const conceptIds = selectedItems.map(item => item.concept_id);
      fetchMathceptDetails(conceptIds);
    }
  };

  // 태그 클릭 핸들러
  const handleTagClick = (tag) => {
    setSearchParams({ 
      description: searchParams.description, 
      math_type: '', 
      concept_type: '', 
      concept_name: '',
      tag: tag 
    });
    setCurrentPage(1);
    fetchMathcepts({ 
      description: searchParams.description, 
      math_type: '', 
      concept_type: '', 
      concept_name: '',
      tag: tag 
    });
    closeDetailModal(); // 모달이 열려있다면 닫기
  };

  return (
    <div style={{ height: '100vh', overflow: 'auto' }}>
      <PageContainer title="매스컨셉 임시 조회">
        {/* 검색 필터 */}
        <Card style={{ marginBottom: '20px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
              {/* 수학 분야 선택 */}
              <div>
                <label style={{ fontSize: '14px', fontWeight: '500', marginBottom: '4px', display: 'block' }}>
                  수학 분야
                </label>
                <select
                  value={searchParams.math_type}
                  onChange={(e) => setSearchParams({ ...searchParams, math_type: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                >
                  {mathTypeOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* 개념 유형 선택 */}
              <div>
                <label style={{ fontSize: '14px', fontWeight: '500', marginBottom: '4px', display: 'block' }}>
                  개념 유형
                </label>
                <select
                  value={searchParams.concept_type}
                  onChange={(e) => setSearchParams({ ...searchParams, concept_type: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                >
                  {conceptTypeOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* 개념명 검색 */}
              <div>
                <label style={{ fontSize: '14px', fontWeight: '500', marginBottom: '4px', display: 'block' }}>
                  개념명
                </label>
                <input
                  type="text"
                  placeholder="검색어 입력"
                  value={searchParams.concept_name}
                  onChange={(e) => setSearchParams({ ...searchParams, concept_name: e.target.value })}
                  onKeyPress={handleKeyPress}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                />
              </div>

              {/* 설명 포함 체크박스 */}
              <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={searchParams.description}
                    onChange={(e) => setSearchParams({ ...searchParams, description: e.target.checked })}
                    style={{ width: '16px', height: '16px' }}
                  />
                  <span style={{ fontSize: '14px' }}>설명 포함</span>
                </label>
              </div>

              {/* 태그 검색 */}
              <div>
                <label style={{ fontSize: '14px', fontWeight: '500', marginBottom: '4px', display: 'block' }}>
                  태그
                </label>
                <input
                  type="text"
                  placeholder="태그명 입력"
                  value={searchParams.tag}
                  onChange={(e) => setSearchParams({ ...searchParams, tag: e.target.value })}
                  onKeyPress={handleKeyPress}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                />
              </div>
            </div>

            {/* 버튼 그룹 */}
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <Button
                variant="secondary"
                onClick={() => setSearchParams({ description: false, math_type: '', concept_type: '', concept_name: '', tag: '' })}
              >
                초기화
              </Button>
              <Button onClick={handleSearch} disabled={loading}>
                <Search size={16} />
                검색
              </Button>
            </div>
          </div>
        </Card>

        {/* 결과 테이블 */}
        <Card>
          <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h3 style={{ fontSize: '16px', fontWeight: '600' }}>
              검색 결과 ({totalCount}건)
            </h3>
            {totalCount > 0 && (
              <Button variant="secondary" size="small" onClick={handleDownloadCSV}>
                <Download size={16} />
                CSV 다운로드
              </Button>
            )}
          </div>

          {loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '60px' }}>
              <Loader className="animate-spin" size={32} />
            </div>
          ) : totalCount > 0 ? (
            <div style={{ overflowX: 'auto' }}>
              <DataGrid
                data={paginatedData}
                columns={columns}
                emptyMessage="검색 결과가 없습니다."
                stickyHeader={false}
                onRowClick={handleRowClick}
              />
              
              {/* 페이지네이션 */}
              {totalCount > itemsPerPage && (
                <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'center', gap: '8px' }}>
                  <Button
                    variant="secondary"
                    size="small"
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                  >
                    이전
                  </Button>
                  <span style={{ padding: '8px 16px', fontSize: '14px' }}>
                    {currentPage} / {Math.ceil(totalCount / itemsPerPage)}
                  </span>
                  <Button
                    variant="secondary"
                    size="small"
                    onClick={() => setCurrentPage(prev => Math.min(Math.ceil(totalCount / itemsPerPage), prev + 1))}
                    disabled={currentPage === Math.ceil(totalCount / itemsPerPage)}
                  >
                    다음
                  </Button>
                </div>
              )}
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '60px', color: '#6b7280' }}>
              검색 조건을 설정하고 검색 버튼을 클릭하세요.
            </div>
          )}
        </Card>

        {/* KaTeX 스타일 추가 */}
        <style>{`
          .katex-inline {
            display: inline-block;
          }
          .katex-inline .katex {
            font-size: 1em;
          }
          /* DataGrid의 테이블 스타일 보완 */
          table {
            table-layout: auto;
            width: 100%;
          }
          th, td {
            text-align: left;
            vertical-align: middle;
          }
          th {
            white-space: nowrap;
          }
          td {
            white-space: normal;
            word-break: break-word;
          }
          /* 행 호버 효과 */
          tbody tr:hover {
            background-color: #f3f4f6;
            cursor: pointer;
          }
        `}</style>
        
        {/* 매스컨셉 상세 정보 모달 - 공통 컴포넌트 사용 (태그 클릭 기능 포함) */}
        <MathceptDetailModal
          isOpen={detailModalOpen}
          onClose={closeDetailModal}
          data={detailData}
          loading={detailLoading}
          renderLatex={renderLatex}
          onTagClick={handleTagClick}
          showCloseButton={true}
          useLoader={true}
          handleAuth={true}
          onDataUpdate={refreshDetailData}
        />
      </PageContainer>
    </div>
  );
}

export default MathceptsTemp; 